import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

// Registrar componentes de gráficos
ChartJS.register(
  CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement, Filler
);

const SearchPage = ({ minimal, clienteId: propClienteId }) => {
    // 🛡️ CERTEZA TÉCNICA: Usamos useParams para que la navegación 'Volver' sepa siempre a qué municipio regresar
    const { municipio } = useParams();
    const { slug } = useParams(); 
    // Creamos la variable con el nombre estándar que hemos acordado
    const puebloIdentificado = slug;
    const [allData, setAllData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);

    // FILTROS
    const [searchText, setSearchText] = useState('');
    const [selectedCalle, setSelectedCalle] = useState('');
    const [yearRange, setYearRange] = useState({ min: 1900, max: new Date().getFullYear() });

    // 1. CARGAR DATOS
    // 1. CARGAR DATOS
useEffect(() => {
    // 🛡️ SOLUCIÓN MULTICLIENTE: Si hay municipio en la URL, pedimos los datos por ese SLUG
    // Esto elimina la dependencia de IDs fijos (1 o 4)
    const endpoint = slug || municipio; 

    if (endpoint) {
        setLoading(true);
        axios.get(`http://localhost:4000/api/v1/difuntos?slug=${endpoint}`)
            .then(res => {
                setAllData(res.data);
                setFilteredData(res.data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Error en la petición API:", err);
                setLoading(false);
            });
    }
}, [slug, municipio]); // Solo se dispara si cambia la URL

    // 2. MOTOR DE FILTRADO (MODO DIAGNÓSTICO - MANTENIDO ÍNTEGRO)
    useEffect(() => {
        let result = allData;

        // 1. FILTRO DE TEXTO INTELIGENTE
        if (searchText) {
            const searchTerms = searchText.toLowerCase().trim().split(/\s+/);
            
            result = result.filter(d => {
                const fullName = `${d.nombre || ''} ${d.apellidos || ''}`.toLowerCase();
                const coincide = searchTerms.every(term => fullName.includes(term));
                
                // --- CHIVATO PARA PICAZO (MANTENIDO) ---
                if (fullName.includes('picazo')) {
                    console.log(`🕵️‍♂️ Encontrado PICAZO: ${fullName}`);
                    console.log(`   ¿Coincide con la búsqueda?: ${coincide}`);
                    console.log(`   Fecha Defunción: ${d.fecha_defuncion}`);
                }
                // ---------------------------
                
                return coincide;
            });
        }

        // 2. FILTRO CALLE
        if (selectedCalle) {
            result = result.filter(d => d.calle === selectedCalle);
        }

        // 3. FILTRO AÑO (MANTENIDO ÍNTEGRO CON TODAS LAS VALIDACIONES)
        result = result.filter(d => {
            const nombreCompleto = `${d.nombre || ''} ${d.apellidos || ''}`.toLowerCase();
            const esPicazo = nombreCompleto.includes('picazo');

            if (!d.fecha_defuncion) {
                if(esPicazo) console.log("   ✅ PICAZO PASA PORQUE NO TIENE FECHA");
                return true;
            }
            
            const year = new Date(d.fecha_defuncion).getFullYear();
            
            if (isNaN(year)) {
                if(esPicazo) console.log("   ✅ PICAZO PASA PORQUE SU FECHA ES INVÁLIDA");
                return true; 
            }

            const entraEnRango = year >= yearRange.min && year <= yearRange.max;
            
            if (esPicazo && !entraEnRango) {
                console.log(`   ❌ PICAZO BLOQUEADO POR EL AÑO: Su año es ${year}, el filtro pide desde ${yearRange.min}`);
            }

            return entraEnRango;
        });

        setFilteredData(result);
    }, [searchText, selectedCalle, yearRange, allData]);

    // 3. PREPARAR DATOS PARA GRÁFICOS (ESTADÍSTICAS - MANTENIDO ÍNTEGRO)
    const stats = useMemo(() => {
        const years = {};
        const months = Array(12).fill(0);
        const streets = {};
        const ages = { '0-18 (Niños)': 0, '19-40 (Jóvenes)': 0, '41-65 (Adultos)': 0, '66-85 (Mayores)': 0, '+85 (Ancianos)': 0 };

        filteredData.forEach(d => {
            if (d.fecha_defuncion) {
                const date = new Date(d.fecha_defuncion);
                const y = date.getFullYear();
                const m = date.getMonth(); 
                years[y] = (years[y] || 0) + 1;
                months[m]++;
            }

            if (d.calle) {
                streets[d.calle] = (streets[d.calle] || 0) + 1;
            }

            if (d.edad) {
                const e = parseInt(d.edad);
                if (e <= 18) ages['0-18 (Niños)']++;
                else if (e <= 40) ages['19-40 (Jóvenes)']++;
                else if (e <= 65) ages['41-65 (Adultos)']++;
                else if (e <= 85) ages['66-85 (Mayores)']++;
                else ages['+85 (Ancianos)']++;
            }
        });

        return { years, months, streets, ages };
    }, [filteredData]);

    // --- CONFIGURACIÓN DE GRÁFICOS (MANTENIDO ÍNTEGRO) ---
    const chartYearsData = {
        labels: Object.keys(stats.years).sort(),
        datasets: [{
            label: 'Defunciones',
            data: Object.keys(stats.years).sort().map(y => stats.years[y]),
            backgroundColor: 'rgba(46, 125, 50, 0.7)',
        }]
    };

    const topStreets = Object.entries(stats.streets)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);
    
    const chartStreetsData = {
        labels: topStreets.map(s => s[0]),
        datasets: [{
            data: topStreets.map(s => s[1]),
            backgroundColor: ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'],
        }]
    };

    const chartMonthsData = {
        labels: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
        datasets: [{
            label: 'Defunciones por Mes',
            data: stats.months,
            borderColor: '#FF9F40',
            backgroundColor: 'rgba(255, 159, 64, 0.5)',
            tension: 0.4,
            fill: true
        }]
    };

    const chartAgesData = {
        labels: Object.keys(stats.ages),
        datasets: [{
            label: 'Grupo de Edad',
            data: Object.values(stats.ages),
            backgroundColor: '#36A2EB',
            indexAxis: 'y'
        }]
    };

    const uniqueCalles = [...new Set(allData.map(d => d.calle).filter(Boolean))].sort();
// PEGA ESTO AQUÍ:
    console.log("🔍 CONTROL DE RUTA:");
    console.log("Variable puebloIdentificado:", puebloIdentificado);
    console.log("Variable slug (de useParams):", slug);
    console.log("Variable d.id (si existe):", filteredData[0]?.id);
    return (
        <div style={{ padding: '20px', maxWidth: '1600px', margin: '0 auto', minHeight: '100vh', backgroundColor: '#f4f6f8' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{ color: '#2c3e50', margin: 0 }}>📊 Analítica y Búsqueda Avanzada</h2>
                {/* ✅ CAMBIO CLAVE: Volver al portal respetando al cliente para que no te eche */}
                <Link 
    to={`/${slug}`} 
    style={{ textDecoration: 'none', color: '#666', fontWeight: 'bold' }}
>
    🏠 Volver al Inicio
</Link>
            </div>

            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', marginBottom: '30px', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                
                <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#555', fontWeight: 'bold' }}>Buscar por Nombre/Apellido:</label>
                    <input 
                        type="text" 
                        placeholder="Ej: García..." 
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                    />
                </div>

                <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#555', fontWeight: 'bold' }}>Filtrar por Calle/Sector:</label>
                    <select 
                        value={selectedCalle} 
                        onChange={e => setSelectedCalle(e.target.value)}
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                    >
                        <option value="">Todas las Calles</option>
                        {uniqueCalles.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', color: '#555', fontWeight: 'bold' }}>Año Defunción (Desde):</label>
                    <input 
                        type="number" 
                        value={yearRange.min}
                        onChange={e => setYearRange({...yearRange, min: parseInt(e.target.value)})}
                        style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ccc' }}
                    />
                </div>
                
                <div style={{ padding: '10px', fontWeight: 'bold', color: '#2E7D32' }}>
                    Resultados: {filteredData.length}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px', marginBottom: '40px' }}>
                
                <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <h4 style={{textAlign:'center', color:'#555'}}>📅 Evolución Histórica</h4>
                    <div style={{ height: '250px' }}>
                        <Bar data={chartYearsData} options={{ maintainAspectRatio: false, responsive: true }} />
                    </div>
                </div>

                <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <h4 style={{textAlign:'center', color:'#555'}}>🏙️ Ocupación por Calles (Top 5)</h4>
                    <div style={{ height: '250px', display: 'flex', justifyContent: 'center' }}>
                        <Doughnut data={chartStreetsData} options={{ maintainAspectRatio: false, responsive: true }} />
                    </div>
                </div>

                <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <h4 style={{textAlign:'center', color:'#555'}}>❄️ Estacionalidad (Meses)</h4>
                    <div style={{ height: '250px' }}>
                        <Line data={chartMonthsData} options={{ maintainAspectRatio: false, responsive: true }} />
                    </div>
                </div>

                <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <h4 style={{textAlign:'center', color:'#555'}}>👥 Demografía</h4>
                    <div style={{ height: '250px' }}>
                        <Bar data={chartAgesData} options={{ indexAxis: 'y', maintainAspectRatio: false, responsive: true }} />
                    </div>
                </div>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <h3 style={{ padding: '20px', margin: 0, backgroundColor: '#2E7D32', color: 'white' }}>📋 Listado Detallado</h3>
                
                <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                            <tr>
                                <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nombre</th>
                                <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Fecha Defunción</th>
                                <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Edad</th>
                                <th style={{ padding: '15px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Ubicación</th>
                                <th style={{ padding: '15px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredData.slice(0, 100).map((d) => (
                                <tr key={d.id} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '12px 15px' }}>{d.nombre} {d.apellidos}</td>
                                    <td style={{ padding: '12px 15px' }}>{d.fecha_defuncion ? d.fecha_defuncion.split('T')[0] : '-'}</td>
                                    <td style={{ padding: '12px 15px' }}>{d.edad}</td>
                                    <td style={{ padding: '12px 15px' }}>{d.calle} - {d.nicho}</td>
                                    <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                                        <Link 
    to={`/${puebloIdentificado}/difunto/${d.id}`}
    style={{ backgroundColor: '#6c5ce7', color: 'white', padding: '6px 12px', borderRadius: '4px', textDecoration: 'none', fontSize: '0.9em' }}
>
    Ver Ficha
</Link>
                                    </td>
                                </tr>
                            ))}
                            {filteredData.length === 0 && (
                                <tr>
                                    <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No se encontraron resultados con estos filtros.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SearchPage;